import { jaLocale, translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  spacing,
  fontSize,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import {
  black,
  gray01,
  gray02,
  gray04,
  gray06,
  primary,
} from 'presentations/views/styles/color';
import * as tracker from 'presentations/views/helpers/analytics';

import Icon from 'presentations/views/components/atoms/Icon';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Link from 'presentations/views/components/atoms/Link';
import Image from 'presentations/views/components/atoms/Image';
import {
  apiPath,
  customerStoryListPath,
  resourceCategoryPath,
  resourceTagListPath,
  resourceTagPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
import { useRequest } from 'presentations/views/hooks/request';
import { ResourceTagItem } from 'presentations/api/schema/resources';
import { useMemo } from 'react';

export interface Props {}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const onClickSocial = () => {
    tracker.setEvent('click sns account', {
      position: 'resource sidebar',
    });
  };

  const tagsRequest = useRequest(apiPath(`/v1/resources/tags`));
  const tags = useMemo(() => {
    if (tagsRequest.isLoading) return null;
    const listItems = [];
    const tags: ResourceTagItem[] = tagsRequest.data.reverse();
    for (let i = 0; i < 5; i++) {
      if (!tags[i]) break;
      listItems.push(tags[i]);
    }
    return listItems;
  }, [tagsRequest.data]);

  const router = useRouter();
  return (
    <>
      <SidebarItem>
        <SidebarItemTitle>
          <SidevarItemTitleIcon name="folder-outline" color="primary" />
          {t('category')}
        </SidebarItemTitle>
        <SiderbarListContainer>
          <CategoryContainer
            href={resourceCategoryPath({
              category: 'productivity',
            })}
          >
            <CategoryIcon name="minus" />
            <CategoryTitle>
              {translate(`resource.category.productivity`)}
            </CategoryTitle>
          </CategoryContainer>
          <CategoryContainer
            href={resourceCategoryPath({
              category: 'integration',
            })}
          >
            <CategoryIcon name="minus" />
            <CategoryTitle>
              {translate(`resource.category.integration`)}
            </CategoryTitle>
          </CategoryContainer>
          <CategoryContainer href={customerStoryListPath()}>
            <CategoryIcon name="minus" />
            <CategoryTitle>
              {translate(`resource.category.usecases`)}
            </CategoryTitle>
          </CategoryContainer>
        </SiderbarListContainer>
      </SidebarItem>
      <SidebarItem>
        <SidebarItemTitle>
          <SidevarItemTitleIcon name="tag-outline" color="primary" />
          {t('tag')}
        </SidebarItemTitle>
        <SiderbarListContainer>
          <TagList>
            {tags ? (
              tags.map((tag) => (
                <TagListItem
                  key={tag.slug}
                  href={resourceTagPath({ tag: tag.slug })}
                >
                  <TagIcon name="tag" />
                  <TagName>{tag.name}</TagName>
                </TagListItem>
              ))
            ) : (
              <>
                <TagListItemLoading>
                  <Skeleton variant="text" width={90} />
                </TagListItemLoading>
                <TagListItemLoading>
                  <Skeleton variant="text" width={150} />
                </TagListItemLoading>
                <TagListItemLoading>
                  <Skeleton variant="text" width={120} />
                </TagListItemLoading>
                <TagListItemLoading>
                  <Skeleton variant="text" width={90} />
                </TagListItemLoading>
              </>
            )}
          </TagList>
          <MoreItem href={resourceTagListPath()}>
            <MoreIcon name="chevron-right" />
            <MoreName>{t('More')}</MoreName>
          </MoreItem>
        </SiderbarListContainer>
      </SidebarItem>

      {router.locale === jaLocale && (
        <SidebarItem>
          <SidebarItemTitle>
            <SidevarItemTitleIcon
              name="share-variant-outline"
              color="primary"
            />
            {t('sns')}
          </SidebarItemTitle>
          <BrandLinkList>
            <BrandLinkListItem>
              <SocialLink
                href="https://twitter.com/jicoohq"
                target="_blank"
                onClick={onClickSocial}
                rel="noopener"
              >
                <SocialLogo
                  src={'mails/logo_twitter.png'}
                  alt={translate('mailer.Footer.Twitter')}
                />
              </SocialLink>
              <SocialLink
                href="https://www.facebook.com/jicoohq"
                target="_blank"
                onClick={onClickSocial}
                rel="noopener"
              >
                <SocialLogo
                  src={'mails/logo_facebook.png'}
                  alt={translate('mailer.Footer.Facebook')}
                />
              </SocialLink>
            </BrandLinkListItem>
          </BrandLinkList>
          <BrandLinkText>{t('sns description')}</BrandLinkText>
        </SidebarItem>
      )}
    </>
  );
}

const SidebarItem = styled.div`
  margin: 0 0 ${spacing(56)} 0;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(28)} 0;
  }
`;

const SidebarItemTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 0 ${spacing(20)} 0;
  margin: 0 0 ${spacing(24)} 0;
  border-bottom: 1px solid ${gray01};

  font-size: ${fontSize(16)};
  line-height: 150%;
  font-weight: bold;

  ${mediaMax.tablet} {
    padding: 0 0 ${spacing(12)} 0;
    font-size: ${fontSize(14)};
  }
`;

const SidevarItemTitleIcon = styled(Icon)`
  margin: 0 ${spacing(8)} 0 0;
  line-height: 24px;

  ${mediaMax.tablet} {
    margin: 0 ${spacing(6)} 0 0;
    font-size: ${fontSize(20)};
    line-height: 20px;
  }
`;

const SiderbarListContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryContainer = styled(Link)`
  color: ${black};
  display: flex;
  flex-direction: row;
  justify-content: middle;
  padding: ${spacing(20)} 0;
  border-bottom: 1px solid ${gray04};

  &:first-of-type {
    padding: 0 0 ${spacing(12)} 0;
  }
`;

const CategoryIcon = styled(Icon)`
  margin: 0 ${spacing(8)} 0 0;
  width: 18px !important;
  height: 18px !important;
  line-height: 22px;

  ${mediaMax.tablet} {
    margin-top: 3px;
    font-size: ${fontSize(12)};
    line-height: 12px;
  }
`;

const CategoryTitle = styled.div`
  font-size: ${fontSize(14)};
  line-height: 160%;
  letter-spacing: 0.05em;

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;

const BrandLinkList = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(24)} 0;
    :last-of-type {
      margin: 0;
    }
    padding: 0 ${spacing(16)} 0 0;
    width: 33.33%;
    display: block;
  }
  ${mediaMax.smartphone} {
    padding: 0;
    width: auto;
    text-align: center;
  }
`;
const BrandLinkListItem = styled.div`
  ${mediaMax.smartphone} {
    display: block;
    margin-bottom: ${spacing(20)};
  }
`;
const BrandLinkText = styled.p`
  margin-top: ${spacing(20)};
  text-align: center;
`;
const SocialLogo = styled(Image)`
  width: 48px;
  height: 48px;
`;
const SocialLink = styled(Link)`
  margin-right: ${spacing(16)};
  :last-of-type {
    margin-right: 0;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${spacing(8)};

  ${mediaMax.tablet} {
    padding: 0;
  }
`;
const TagListItem = styled(Link)`
  color: ${gray01};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${gray04};
  border-radius: ${borderRadius(16)};
  margin-right: ${spacing(8)};
  margin-bottom: ${spacing(8)};
  padding: ${spacing(8)};
`;
const TagListItemLoading = styled.div`
  margin-right: ${spacing(8)};
  margin-bottom: ${spacing(8)};
`;
const TagIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  color: ${gray02};
  width: 14px !important;
  height: 14px !important;
`;
const TagName = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;
const MoreItem = styled(Link)`
  color: ${black};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const MoreIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
`;
const MoreName = styled.div``;
